import React, { useState, useEffect } from "react";
import emailjs from "@emailjs/browser";
import "./Contact.css";
import Header from "./Header";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

// Initialize EmailJS with your public key
emailjs.init("3xhaHivrzwAe43cXWvNmF");

const Contact = () => {
  const MOBILE_SCREEN_SIZE = 768;
  
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_SCREEN_SIZE);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    from_name: "",
    from_email: "",
    from_phone: "",
    from_message: "",
    services: [], // Store multiple selected services
  });
  const [phoneNumber, setPhoneNumber] = useState("");

  const [status, setStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [debugInfo, setDebugInfo] = useState("");

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_SCREEN_SIZE);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhoneChange = (value) => {
    setPhoneNumber(value);
  };

  const handleServiceSelect = (e) => {
    const selectedService = e.target.value;
    if (selectedService && !formData.services.includes(selectedService)) {
      setFormData({
        ...formData,
        services: [...formData.services, selectedService],
      });
    }
  };

  const removeService = (service) => {
    setFormData({
      ...formData,
      services: formData.services.filter((s) => s !== service),
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus("");
    setDebugInfo("");

    // Prepare the template parameters
    const templateParams = {
      from_name: formData.from_name,
      from_email: formData.from_email,
      from_phone: phoneNumber, // Use the phone number from the phone input component
      from_message: formData.from_message,
      services: formData.services.join(", "),
    };

    // Log the parameters for debugging
    console.log("Sending email with params:", templateParams);
    setDebugInfo(`Attempting to send email with params: ${JSON.stringify(templateParams)}`);

    // Send the email using EmailJS
    emailjs
      .send(
        "service_p9e4kf3",
        "template_1yl43h9",
        templateParams,
        "HH1PM3IsGOGQDMnVT"
      )
      .then(
        (response) => {
          console.log("SUCCESS! EmailJS Response:", response);
          setDebugInfo(`Success! Response: ${JSON.stringify(response)}`);
          setStatus("Message sent successfully!");
          setFormData({
            from_name: "",
            from_email: "",
            from_phone: "",
            from_message: "",
            services: [],
          });
          setPhoneNumber("");
        },
        (error) => {
          console.error("FAILED! EmailJS Error:", error);
          setDebugInfo(`Error: ${JSON.stringify(error)}`);
          setStatus(`Failed to send message: ${error.text || error.message || "Unknown error"}`);
        }
      )
      .finally(() => {
        setIsSubmitting(false);
        setTimeout(() => setStatus(""), 5000);
      });
  };

  return (
    <>
      <Header hideScheduleButton={true} hideContact={true} />
      <div className="contact-page">
        <div className="contact-container-career">
          <div className="contact-info-section">
            <h1 className="info-title">Get In Touch</h1>
            <p className="info-description">
              Connect with us today and unlock your business's potential.
            </p>
            <div className="info-cards">
              <div className="info-card">
                <MapPin size={24} />
                <div className="info-card-content">
                  <h3>Address</h3>
                  <p>Corporate Office: 828 1st Street, Secaucus, New Jersey</p>
                  <p>Global Development Center: B-13 Sector-1, Noida, U.P.</p>
                </div>
              </div>
              <div className="info-card">
                <Phone size={24} />
                <div className="info-card-content">
                  <h3>Phone</h3>
                  <p>847-847-4027</p>
                </div>
              </div>
              <div className="info-card">
                <Mail size={24} />
                <div className="info-card-content">
                  <h3>Email Us</h3>
                  <p>Sanjeev.varshney@cyntralabs.com</p>
                </div>
              </div>
            </div>
            <a 
              href="https://outlook.office365.com/owa/calendar/CyntraMeeting@cyntralabs.com/bookings/"
              target="_blank"
              rel="noopener noreferrer"
              className="book-appointment-btn"
            >
              Book an Appointment
            </a>
          </div>

          <div className="contact-form-section">
            <h2 className="form-title">Let's start something amazing together</h2>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                name="from_name"
                placeholder="Name"
                className="form-input"
                value={formData.from_name}
                onChange={handleChange}
                required
              />
              <input
                type="email"
                name="from_email"
                placeholder="Email"
                className="form-input"
                value={formData.from_email}
                onChange={handleChange}
                required
              />
              
              <div className="phone-input-container" style={{ marginBottom: '20px' }}>
                <PhoneInput
                  country={'us'}
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  inputProps={{
                    name: 'from_phone',
                    required: true,
                    className: 'form-input',
                    style: {
                      width: '100%',
                      height: '48px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      paddingLeft: '3.5rem',
                      fontSize: '16px'
                    }
                  }}
                  containerStyle={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  buttonStyle={{
                    border: '1px solid #ddd',
                    borderRadius: '4px 0 0 4px',
                    backgroundColor: 'white',
                    height: '48px',
                    padding: '0 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  dropdownStyle={{
                    width: '300px',
                    maxHeight: '300px'
                  }}
                />
              </div>

              <label className="form-label">Choose services you're interested in</label>
              <select
                onChange={handleServiceSelect}
                className="form-input"
                defaultValue=""
              >
                <option value="" disabled>Select a service</option>
                <option value="Integration Solutions">Integration Solutions</option>
                <option value="Enterprise Solutions">Enterprise Solutions</option>
                <option value="Cloud-Native Excellence">Cloud-Native Excellence</option>
                <option value="Application Support">Application Support</option>
                <option value="ERP Consulting">ERP Consulting</option>
              </select>

              <div className="selected-services">
                {formData.services.map((service, index) => (
                  <div key={index} className="service-chip">
                    {service}
                    <span
                      className="remove-chip"
                      onClick={() => removeService(service)}
                    >
                      ×
                    </span>
                  </div>
                ))}
              </div>

              <textarea
                name="from_message"
                placeholder="Tell us about your project"
                className="form-textarea"
                value={formData.from_message}
                onChange={handleChange}
                required
                rows="5"
              ></textarea>

              <button 
                type="submit" 
                className="form-submit" 
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending..." : "Submit Enquiry →"}
              </button>
              {status && (
                <p className={`form-status ${status.includes("success") ? "success" : "error"}`}>
                  {status}
                </p>
              )}
              
              {/* Debug information - you can remove this in production */}
              {/* {debugInfo && (
                <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '5px', fontSize: '12px', overflow: 'auto' }}>
                  <strong>Debug Info:</strong>
                  <pre>{debugInfo}</pre>
                </div>
              )} */}
            </form>
          </div>
        </div>

        <motion.footer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut", delay: 1 }}
          className="contact-footer"
        >
          © 2025 CyntraLabs. All rights reserved. | Privacy Policy | Terms of
          Service | Data Privacy Terms
        </motion.footer>
      </div>
    </>
  );
};

export default Contact;
